import { useTableActionContext } from '@aibees/elephant/context';
import { useCurrentOwner, useGetOwnership } from '@aibees/elephant/hooks';
import { classnames } from '@aibees/shared/utils';
import { Autocomplete, TextField } from '@mui/material';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { OwnerSelectProps as Props } from './types';
import { SearchProvider, useSearch } from '@aibees/shared/context';

const OwnerSelectContent = memo(
  ({
    className,
    item,
    onUserSelect: _onUserSelect,
    selectedUser: propsSelectedUser,
    searchType,
  }: Props) => {
    const { setSelectedOwner, selectedOwner } = useTableActionContext();
    const { setSearch, rootRef } = useSearch();
    const [inputValue, setInputValue] = useState('');
    const isInternalChangeRef = useRef(false);
    const resolvedOwner = useCurrentOwner({
      selectedUser: propsSelectedUser,
      item,
      searchType,
    });
    const { options } = useGetOwnership(resolvedOwner ?? undefined);

    useEffect(() => {
      if (propsSelectedUser) {
        setSelectedOwner(propsSelectedUser.id);
      } else if (resolvedOwner?.id) {
        setSelectedOwner(resolvedOwner?.id);
      } else if (options) {
        setSelectedOwner(options[0]?.value);
      }
    }, [propsSelectedUser, setSelectedOwner, resolvedOwner?.id]);

    const selectedOption = useMemo(() => {
      return options.find((opt) => opt.value === selectedOwner) || null;
    }, [options, selectedOwner]);
    const displayValue = useMemo(() => {
      if (selectedOption && inputValue === selectedOption.label) {
        return selectedOption;
      }
      return null;
    }, [selectedOption, inputValue]);

    const autocompleteSlotProps = useMemo(
      () => ({
        paper: {
          ref: rootRef,
          sx: SX.paper,
        },
      }),
      [rootRef]
    );

    useEffect(() => {
      if (isInternalChangeRef.current) {
        isInternalChangeRef.current = false;
        return;
      }

      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else {
        setInputValue('');
      }
    }, [selectedOption]);

    const handleChange = (
      _event: unknown,
      newValue: { label: string; value: string } | null
    ) => {
      isInternalChangeRef.current = true;
      setSelectedOwner(newValue?.value ?? '');

      if (newValue) {
        setInputValue(newValue.label);
      } else {
        setInputValue('');
      }
    };

    const handleInputChange = (
      _event: unknown,
      value: string,
      reason?: string
    ) => {
      if (reason === 'input' || reason === 'clear' || reason === 'reset') {
        isInternalChangeRef.current = true;
        setInputValue(value);
        setSearch(value);

        if (value === '' && selectedOption) {
          setSelectedOwner('');
        }
      }
    };

    return (
      <Styled.Wrapper
        className={classnames('OwnerSelect', className)}
        sx={SX.root}
      >
        <Autocomplete
          fullWidth
          size="medium"
          options={options}
          value={displayValue as { label: string; value: string } | undefined}
          inputValue={inputValue}
          onChange={handleChange}
          onInputChange={handleInputChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          clearOnBlur={false}
          selectOnFocus={false}
          disableClearable
          slotProps={autocompleteSlotProps}
          renderInput={(params) => (
            <TextField {...params} placeholder="Choose or search owner" />
          )}
        />
      </Styled.Wrapper>
    );
  }
);

OwnerSelectContent.displayName = 'OwnerSelectContent';

export const OwnerSelect = memo((props: Props) => {
  return (
    <SearchProvider>
      <OwnerSelectContent {...props} />
    </SearchProvider>
  );
});

OwnerSelect.displayName = 'OwnerSelect';
